import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiltersSection } from "./FiltersSection";
import { ProductTable } from "./ProductTable";
import { SearchSection } from "./SearchSection";
import { StatsSection } from "./StatsSection";
import { useAlgoliaSearch } from "./hooks/useAlgoliaSearch";
import { useBrands } from "./hooks/useBrands";
import { Product } from "@/types/product";

interface ExplorerContentProps {
  onProductsFiltered: (filteredProducts: Product[]) => void;
  onFilterChange: (filters: string) => void;
  onSearch: (query: string) => void;
}

export const ExplorerContent = ({ 
  onProductsFiltered, 
  onFilterChange,
  onSearch 
}: ExplorerContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([
    Number(searchParams.get("minPrice")) || 1,
    Number(searchParams.get("maxPrice")) || 5000
  ]);
  const [selectedField, setSelectedField] = useState<string>(searchParams.get("field") || "title");
  const [filterTerm, setFilterTerm] = useState<string>(searchParams.get("query") || "");
  const [selectedBrand, setSelectedBrand] = useState<string>(searchParams.get("brand") || "all");
  const [selectedRating, setSelectedRating] = useState<string>(searchParams.get("rating") || "all_products");
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>(
    searchParams.get("availability")?.split(",") || ["all"]
  );
  const [selectedDomain, setSelectedDomain] = useState<string>(searchParams.get("domain") || "all");

  const { allBrands } = useBrands();
  const { products, isLoading } = useAlgoliaSearch({
    priceRange,
    selectedBrand,
    selectedRating,
    selectedAvailability,
    selectedDomain,
    currentQuery: filterTerm
  });

  // Get unique domains from products
  const availableDomains = Array.from(new Set(products.map(p => p.domain))).sort();

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    if (filterTerm) {
      params.set("query", filterTerm);
    } else {
      params.delete("query");
    }
    setSearchParams(params, { replace: true });
    onSearch(filterTerm);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
      <FiltersSection
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        products={products}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        allBrands={allBrands}
        selectedRating={selectedRating}
        setSelectedRating={setSelectedRating}
        selectedAvailability={selectedAvailability}
        setSelectedAvailability={setSelectedAvailability}
        selectedDomain={selectedDomain}
        setSelectedDomain={setSelectedDomain}
        availableDomains={availableDomains}
      />

      <div className="md:col-span-3">
        <SearchSection
          selectedField={selectedField}
          setSelectedField={setSelectedField}
          filterTerm={filterTerm}
          setFilterTerm={setFilterTerm}
          handleSearch={handleSearch}
        />

        <StatsSection products={products} />

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        ) : (
          <ProductTable
            products={products}
            showingLimited={false}
          />
        )}
      </div>
    </div>
  );
};